import { Head } from "@inertiajs/react"
import {
  AlignLeft,
  CalendarDays,
  Globe,
  Phone,
  PhoneForwarded,
  PhoneIncoming,
  PhoneOff,
  ScrollText,
  Voicemail,
  Volume2,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { RetellWebClient } from "retell-client-js-sdk"

import {
  ActivateAgentModal,
  type LandingTrialInfo,
} from "@/components/landing/activate-agent-modal"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { MovingBorder } from "@/components/ui/moving-border"
import { useForceLightMode } from "@/hooks/use-appearance"
import { assistantTheme } from "@/lib/assistant"
import { cn } from "@/lib/utils"
import { landingPageCtaPath, landingPageTestCallPath } from "@/routes"

// IMP-238: the landing page is a light-only marketing page, but the shadcn
// `outline` button carries dark-mode overrides (dark:bg-input/30 etc.). Pin
// those buttons to their light appearance in both themes so they don't flip
// when a visitor's app/system is in dark mode.
const FORCE_LIGHT_OUTLINE =
  "border-zinc-200 bg-white text-zinc-900 hover:bg-zinc-50 hover:text-zinc-900 dark:border-zinc-200 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-50 dark:hover:text-zinc-900"

// ── Types ───────────────────────────────────────────────────────────────────

interface AccountInfo {
  name: string
  // IMP-252: admin heading override; rendered verbatim when present, else the
  // generated hero heading is used.
  heading: string | null
  // IMP-261: admin subheading override; same fallback to the generated subtitle.
  subheading: string | null
  business_name: string | null
  website: string | null
  address: string | null
  phone: string | null
  description: string | null
  services: string | null
  // IMP-239: when false, hide the hero CTA and swap the closing CTA for a
  // "Get in touch" mailto.
  cta_enabled: boolean
  // Three industry items that open the hero subtitle.
  industry_phrases: string[]
}

interface PartnerInfo {
  name: string
  support_email: string | null
  logo_url: string | null
  logo_wordmark_url: string | null
  // IMP-297: partner-managed accounts activate through the partner, so the
  // self-serve "Activate your receptionist" button is hidden for them.
  partner_managed: boolean
  // IMP-500: the partner's agent-identity theme id (see lib/assistant.ts). The
  // page's whole mist ramp derives from its swatch.
  assistant_color: string
}

interface AgentInfo {
  uid: string
  state: string
  retell_ready: boolean
  // IMP-249: the provisional agent's free demo minutes are spent.
  demo_exhausted: boolean
}

interface PricingInfo {
  retail_price_cents: number
  currency: string
  // IMP-270: present when the product bundles a monthly minutes allowance.
  included_minutes?: number
  // Marked-up per-minute overage rate, in dollars (4 decimal places).
  overage_retail_per_minute?: number
  // IMP-394: present when a free trial is offered for this account.
  trial?: LandingTrialInfo | null
}

interface StripeInfo {
  publishable_key: string
  connected_account_id: string
}

// Retell streams its full transcript each update as {role, content}; the
// panel renders it as caller/agent chat bubbles.
type CallState = "idle" | "connecting" | "active" | "ended" | "demo_complete"

interface TranscriptLine {
  id: number
  speaker: "agent" | "caller"
  text: string
}

// ── Call panel ────────────────────────────────────────────────────────────────

function formatElapsed(s: number) {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec.toString().padStart(2, "0")}`
}

function CallPanel({
  businessName,
  ready,
  callState,
  transcript,
  elapsed,
  error,
  onStart,
  onEnd,
}: {
  businessName: string
  ready: boolean
  callState: CallState
  transcript: TranscriptLine[]
  elapsed: number
  error: string | null
  onStart: () => void
  onEnd: () => void
}) {
  const transcriptRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight
    }
  }, [transcript])

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-zinc-100 px-5 py-4">
        <div className="min-w-0 flex-1">
          {/* IMP-238: pin to dark text — without it the name inherits the
              theme `foreground` token and turns light on the white widget
              header under dark mode. */}
          <p className="truncate text-sm leading-tight font-semibold text-zinc-900">
            {businessName}
          </p>
          <p className="text-xs text-zinc-400">AI Receptionist</p>
        </div>
        {callState === "active" && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 tabular-nums">
            <span className="size-1.5 animate-pulse rounded-full bg-emerald-500" />
            {formatElapsed(elapsed)}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="relative min-h-0 flex-1">
        {/* Idle */}
        {callState === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-8 text-center">
            <div className="flex size-24 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50">
              <Phone className="size-9 text-zinc-400" />
            </div>
            {ready ? (
              <>
                <div>
                  <p className="font-semibold text-zinc-900">
                    Hear it for yourself
                  </p>
                  <p className="mt-1 max-w-xs text-sm text-zinc-400">
                    Already trained on {businessName}. Start a call to hear it
                    in action.
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-md p-[1.5px]">
                  <div className="absolute inset-0">
                    <MovingBorder duration={4000} rx="15%" ry="15%">
                      <div className="size-16 bg-[radial-gradient(ellipse_at_center,var(--mist-400)_20%,transparent_70%)] opacity-90" />
                    </MovingBorder>
                  </div>
                  <Button
                    size="lg"
                    variant="outline"
                    className={cn("relative gap-2", FORCE_LIGHT_OUTLINE)}
                    onClick={onStart}
                  >
                    <Phone className="size-4" />
                    Start test call
                  </Button>
                </div>
              </>
            ) : (
              <div>
                <p className="font-semibold text-zinc-900">
                  Getting things ready
                </p>
                <p className="mt-1 max-w-xs text-sm text-zinc-400">
                  The receptionist for {businessName} is still being prepared.
                  Check back in a minute.
                </p>
              </div>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        )}

        {/* Connecting */}
        {callState === "connecting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center">
            {/* IMP-500: mist-600, not 500 — 500 on a 100 ground is 2.7:1 under
                the ramp's original violet and worse under some identities, and
                every other icon on the page is already 600. */}
            <div className="bg-mist-100 flex size-20 items-center justify-center rounded-full">
              <Phone className="text-mist-600 size-8 animate-pulse" />
            </div>
            <div>
              <p className="font-semibold text-zinc-900">Connecting…</p>
              <p className="mt-1 text-sm text-zinc-400">{businessName}</p>
            </div>
          </div>
        )}

        {/* Demo complete (IMP-249): free minutes spent */}
        {callState === "demo_complete" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-8 text-center">
            <div className="flex size-24 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50">
              <PhoneOff className="size-9 text-zinc-400" />
            </div>
            <div>
              <p className="font-semibold text-zinc-900">Demo complete</p>
              <p className="mt-1 max-w-xs text-sm text-zinc-400">
                This demo&rsquo;s free minutes are up. Activate your
                receptionist to start taking real calls for {businessName}.
              </p>
            </div>
          </div>
        )}

        {/* Transcript */}
        {(callState === "active" || callState === "ended") && (
          <div
            ref={transcriptRef}
            className="absolute inset-0 space-y-3 overflow-y-auto px-4 py-4"
          >
            {transcript.length === 0 && (
              <p className="text-center text-xs text-zinc-400 italic">
                Waiting for the receptionist to speak…
              </p>
            )}
            {transcript.map((line) => (
              <div
                key={line.id}
                className={cn(
                  "flex w-full",
                  line.speaker === "caller" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                    line.speaker === "agent"
                      ? "rounded-tl-sm bg-zinc-100 text-zinc-800"
                      : "bg-mist-600 rounded-tr-sm text-white",
                  )}
                >
                  {line.text}
                </div>
              </div>
            ))}
            {callState === "active" && transcript.length > 0 && (
              <div className="flex max-w-[85%]">
                <div className="rounded-2xl rounded-tl-sm bg-zinc-100 px-3.5 py-3">
                  <div className="flex items-center gap-1">
                    <span className="size-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:0ms]" />
                    <span className="size-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:150ms]" />
                    <span className="size-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-100 px-5 py-4">
        {callState === "idle" && (
          <p className="text-center text-xs text-zinc-400">
            No phone required — uses your browser microphone
          </p>
        )}
        {callState === "connecting" && (
          <div className="flex justify-center">
            <button
              onClick={onEnd}
              className="flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-zinc-600"
            >
              <PhoneOff className="size-3.5" />
              Cancel
            </button>
          </div>
        )}
        {callState === "active" && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Volume2 className="size-3.5" />
              Live transcript
            </div>
            <Button
              size="sm"
              variant="outline"
              className={cn("h-8 gap-1.5", FORCE_LIGHT_OUTLINE)}
              onClick={onEnd}
            >
              <PhoneOff className="size-3.5" />
              End call
            </Button>
          </div>
        )}
        {callState === "ended" && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-zinc-400">
              Call ended · {formatElapsed(elapsed)}
            </p>
            <Button
              size="sm"
              variant="outline"
              className={cn("h-8", FORCE_LIGHT_OUTLINE)}
              onClick={onStart}
            >
              Call again
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Brand (nav + footer) ───────────────────────────────────────────────────────

function Brand({
  partner,
  className,
}: {
  partner: PartnerInfo | null
  className?: string
}) {
  if (partner?.logo_wordmark_url) {
    return (
      <img
        src={partner.logo_wordmark_url}
        alt={partner.name}
        className={cn("h-7 w-auto", className)}
      />
    )
  }
  if (partner?.logo_url) {
    // Logomark stands alone — no name text alongside it (IMP-257).
    return (
      <img
        src={partner.logo_url}
        alt={partner.name}
        className={cn("h-7 w-auto", className)}
      />
    )
  }
  return (
    <span
      className={cn("text-lg font-medium text-zinc-900", className)}
      style={{ fontFamily: "'Hedvig Letters Serif', serif" }}
    >
      {partner?.name ?? "Imperative"}
    </span>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function joinPhrases(phrases: string[]): string {
  if (phrases.length <= 1) return phrases[0] ?? ""
  return `${phrases.slice(0, -1).join(", ")}, and ${phrases[phrases.length - 1]}`
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LandingPage({
  account,
  partner,
  agent,
  pricing,
  stripe,
}: {
  account: AccountInfo
  partner: PartnerInfo | null
  agent: AgentInfo | null
  pricing: PricingInfo | null
  stripe: StripeInfo | null
}) {
  // IMP-292: this is a light-only marketing page — pin it out of the app's
  // OS-driven dark mode so portaled UI (the activate modal) stays light too.
  useForceLightMode()

  // IMP-500: the partner's identity colour, resolved through the same shared
  // palette the Identity settings screen and the hub landing page read.
  const brandColor = assistantTheme(partner?.assistant_color).swatch

  const [callState, setCallState] = useState<CallState>(
    agent?.demo_exhausted ? "demo_complete" : "idle",
  )
  const [transcript, setTranscript] = useState<TranscriptLine[]>([])
  const [elapsed, setElapsed] = useState(0)
  const [callError, setCallError] = useState<string | null>(null)
  const [activateOpen, setActivateOpen] = useState(false)

  const webClientRef = useRef<RetellWebClient | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function stopTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  function teardownClient() {
    webClientRef.current?.stopCall()
    webClientRef.current = null
  }

  useEffect(() => {
    return () => {
      teardownClient()
      stopTimer()
    }
  }, [])

  const businessName = account.business_name ?? account.name
  const callReady = agent?.retell_ready ?? false
  const canActivate = Boolean(pricing && stripe && agent)
  // IMP-297: partner-managed accounts activate through the partner, so the
  // self-serve Activate button is hidden entirely. Non-partner-managed accounts
  // keep the normal button (which still disables itself when not yet activatable).
  const partnerManaged = Boolean(partner?.partner_managed)
  // IMP-394: when a free trial is offered, advertise it under the Activate CTA.
  const trial = pricing?.trial ?? null
  const heroSubtitle = `${joinPhrases(account.industry_phrases)} don't wait for a callback. We trained an AI receptionist on ${businessName} — try it now, it answers like it's already on your team.`

  const uid =
    typeof window !== "undefined"
      ? (window.location.pathname.split("/").pop() ?? "")
      : ""

  // IMP-331: record a CTA click against this session's visit. Fire-and-forget —
  // never block or break the interaction it's attached to.
  function trackCta(cta: "activate" | "test_call" | "contact_us") {
    const csrf = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    void fetch(landingPageCtaPath(uid), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf ?? "",
      },
      credentials: "same-origin",
      body: JSON.stringify({ cta }),
      keepalive: true,
    }).catch(() => undefined)
  }

  function openActivate() {
    trackCta("activate")
    setActivateOpen(true)
  }

  async function startCall() {
    trackCta("test_call")
    setCallError(null)
    setTranscript([])
    setElapsed(0)
    setCallState("connecting")
    try {
      const csrf = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")
      const response = await fetch(landingPageTestCallPath(uid), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf ?? "",
        },
        credentials: "same-origin",
      })
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          error?: string
          demo_complete?: boolean
        }
        // IMP-249: free demo minutes are spent — show the "Demo complete"
        // state rather than surfacing an error.
        if (body.demo_complete) {
          setCallState("demo_complete")
          return
        }
        throw new Error(body.error ?? "Couldn't start the test call.")
      }
      const { access_token } = (await response.json()) as {
        access_token: string
      }

      const client = new RetellWebClient()
      webClientRef.current = client
      client.on(
        "update",
        (update: {
          transcript?: { role: "agent" | "user"; content: string }[]
        }) => {
          if (!Array.isArray(update.transcript)) return
          setTranscript(
            update.transcript.map((turn, i) => ({
              id: i,
              speaker: turn.role === "agent" ? "agent" : "caller",
              text: turn.content,
            })),
          )
        },
      )
      client.on("call_started", () => {
        setCallState("active")
        stopTimer()
        intervalRef.current = setInterval(() => setElapsed((e) => e + 1), 1000)
      })
      client.on("call_ended", () => {
        setCallState("ended")
        stopTimer()
        webClientRef.current = null
      })
      client.on("error", (err: unknown) => {
        setCallError(
          err instanceof Error ? err.message : "The test call hit an error.",
        )
        setCallState("idle")
        stopTimer()
        teardownClient()
      })

      // Retell's startCall prompts for microphone permission itself.
      await client.startCall({ accessToken: access_token })
    } catch (err) {
      setCallError(err instanceof Error ? err.message : String(err))
      setCallState("idle")
      teardownClient()
      stopTimer()
    }
  }

  function endCall() {
    teardownClient()
    stopTimer()
    setCallState((s) => (s === "active" || s === "ended" ? "ended" : "idle"))
  }

  return (
    <>
      <Head title={`${businessName} AI Receptionist`}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Hedvig+Letters+Serif:opsz@12..24&display=swap"
          rel="stylesheet"
        />
      </Head>

      {/* IMP-500: the mist ramp this page is built on derives from the
          partner's identity colour, the same selection that tints the hub
          landing page. An account with no partner leaves --lp-brand at
          .lp-palette's own default and renders the ramp it always did. */}
      <div
        className="lp-palette flex min-h-screen flex-col bg-white"
        style={
          partner
            ? ({ "--lp-brand": brandColor } as React.CSSProperties)
            : undefined
        }
      >
        {/* Nav */}
        <header className="bg-white">
          <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-6">
            <Brand partner={partner} />
            {partner?.support_email && (
              <Button
                variant="outline"
                size="sm"
                className={FORCE_LIGHT_OUTLINE}
                asChild
              >
                <a
                  href={`mailto:${partner.support_email}`}
                  onClick={() => trackCta("contact_us")}
                >
                  Contact us
                </a>
              </Button>
            )}
          </div>
        </header>

        {/* Hero */}
        <section className="flex min-h-[calc(100svh-56px)] flex-1 items-center">
          <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-10 px-6 py-16 md:flex-row md:gap-16">
            {/* Left — copy */}
            <div className="flex w-full flex-col gap-8 md:flex-1">
              <div>
                <h1
                  className="text-4xl leading-tight font-medium tracking-tight text-zinc-900 lg:text-5xl"
                  style={{ fontFamily: "'Hedvig Letters Serif', serif" }}
                >
                  {account.heading ?? (
                    <>
                      How much revenue is{" "}
                      <span className="text-mist-600">{businessName}</span>{" "}
                      sending to voicemail?
                    </>
                  )}
                </h1>
                <p className="mt-5 text-lg leading-relaxed text-zinc-500">
                  {account.subheading ?? heroSubtitle}
                </p>
              </div>

              {account.cta_enabled && !partnerManaged && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-4">
                    <Button
                      size="lg"
                      className="bg-zinc-900 text-white shadow-sm hover:bg-zinc-700"
                      disabled={!canActivate}
                      onClick={openActivate}
                    >
                      {trial
                        ? "Start your free trial"
                        : "Activate your receptionist"}
                    </Button>
                  </div>
                  {trial && (
                    <p className="text-sm text-zinc-500">
                      {trial.days}-day free trial, then billed monthly. Cancel
                      anytime.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Right — call panel with animated gradient */}
            <div className="bg-mist-50 relative h-[640px] w-full overflow-hidden rounded-3xl border border-black/5 md:flex-1">
              <style>{`
                @keyframes grad-blob-a {
                  0%, 100% { transform: translate(0%, 0%) scale(1); }
                  40%       { transform: translate(18%, -14%) scale(1.12); }
                  70%       { transform: translate(-10%, 16%) scale(0.92); }
                }
                @keyframes grad-blob-b {
                  0%, 100% { transform: translate(0%, 0%) scale(1); }
                  35%       { transform: translate(-18%, 12%) scale(0.88); }
                  65%       { transform: translate(14%, -16%) scale(1.14); }
                }
              `}</style>
              <div
                className="absolute -top-1/3 -left-1/4 h-3/4 w-3/4 rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse at center, var(--mist-200), transparent 70%)",
                  filter: "blur(48px)",
                  animation: "grad-blob-a 14s ease-in-out infinite",
                }}
              />
              <div
                className="absolute -right-1/4 -bottom-1/3 h-3/4 w-3/4 rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse at center, var(--mist-500), transparent 70%)",
                  filter: "blur(48px)",
                  animation: "grad-blob-b 18s ease-in-out infinite",
                }}
              />
              <div className="relative h-full p-8">
                <CallPanel
                  businessName={businessName}
                  ready={callReady}
                  callState={callState}
                  transcript={transcript}
                  elapsed={elapsed}
                  error={callError}
                  onStart={() => void startCall()}
                  onEnd={endCall}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-6xl px-6">
            <div className="mb-16">
              <p className="text-mist-600 mb-3 text-sm font-medium">Benefits</p>
              <h2
                className="mb-2 text-3xl leading-snug font-medium tracking-tight text-zinc-900 lg:text-4xl"
                style={{ fontFamily: "'Hedvig Letters Serif', serif" }}
              >
                A front desk that never misses
              </h2>
              <p className="max-w-xl leading-relaxed text-zinc-500">
                Your business is always open, even when your office isn&rsquo;t.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {/* Never miss a call */}
              <div>
                <div
                  className="mb-6 h-72 overflow-hidden rounded-2xl px-5 pt-5"
                  style={{
                    background:
                      "radial-gradient(ellipse at 30% 40%, var(--mist-200) 0%, var(--mist-100) 60%, var(--mist-50) 100%)",
                  }}
                >
                  <div className="h-full overflow-hidden rounded-t-xl bg-white shadow-[inset_0_2px_8px_rgba(0,0,0,0.06)]">
                    <div className="p-5">
                      <div className="mb-4 flex items-center justify-between">
                        <p className="text-xs font-semibold text-zinc-900">
                          Incoming calls
                        </p>
                        <p className="text-xs text-zinc-400">Today</p>
                      </div>
                      <div className="space-y-2">
                        {[
                          { name: "Rachel Moore", time: "8:14 AM" },
                          { name: "Unknown caller", time: "11:02 AM" },
                          { name: "David Park", time: "2:45 PM" },
                          { name: "Sarah Kim", time: "4:30 PM" },
                          { name: "Unknown caller", time: "6:48 PM" },
                        ].map(({ name, time }) => (
                          <div
                            key={time}
                            className="flex items-center gap-3 rounded-lg border border-zinc-100 px-3 py-2.5"
                          >
                            <div className="bg-mist-50 flex size-7 shrink-0 items-center justify-center rounded-full">
                              <PhoneIncoming className="text-mist-600 size-3" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-medium text-zinc-900">
                                {name}
                              </p>
                              <p className="text-[10px] text-zinc-400">
                                {time}
                              </p>
                            </div>
                            <span className="text-mist-700 bg-mist-100 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium">
                              Answered
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <h3 className="mb-2 text-xl font-semibold text-zinc-900">
                  Never miss a call
                </h3>
                <p className="text-sm leading-relaxed text-zinc-500">
                  A potential customer who hits voicemail calls the next
                  business on the list. Your receptionist answers every call,
                  around the clock, and captures the opportunity.
                </p>
              </div>

              {/* Free your team */}
              <div>
                <div
                  className="mb-6 h-72 overflow-hidden rounded-2xl px-5 pt-5"
                  style={{
                    background:
                      "radial-gradient(ellipse at 30% 40%, var(--mist-200) 0%, var(--mist-100) 60%, var(--mist-50) 100%)",
                  }}
                >
                  <div className="h-full overflow-hidden rounded-t-xl bg-white shadow-[inset_0_2px_8px_rgba(0,0,0,0.06)]">
                    <div className="space-y-3 px-4 py-5">
                      {[
                        {
                          speaker: "caller",
                          text: "Do you offer free consultations?",
                        },
                        {
                          speaker: "agent",
                          text: "Yes, we offer a free 15-minute consultation for new clients. I can book one for you now if you'd like.",
                        },
                        {
                          speaker: "caller",
                          text: "That would be great, thanks.",
                        },
                        {
                          speaker: "agent",
                          text: "Perfect. Can I take your name and a time that works for you?",
                        },
                      ].map(({ speaker, text }, i) => (
                        <div
                          key={i}
                          className={cn(
                            "flex w-full",
                            speaker === "caller"
                              ? "justify-end"
                              : "justify-start",
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed",
                              speaker === "agent"
                                ? "rounded-tl-sm bg-zinc-100 text-zinc-800"
                                : "bg-mist-100 text-mist-700 rounded-tr-sm",
                            )}
                          >
                            {text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <h3 className="mb-2 text-xl font-semibold text-zinc-900">
                  Free your team
                </h3>
                <p className="text-sm leading-relaxed text-zinc-500">
                  Your staff shouldn&rsquo;t spend their day answering the same
                  questions on repeat. Your receptionist handles the routine
                  calls so your team can stay focused.
                </p>
              </div>

              {/* Always in the loop */}
              <div>
                <div
                  className="mb-6 h-72 overflow-hidden rounded-2xl px-5 pt-5"
                  style={{
                    background:
                      "radial-gradient(ellipse at 30% 40%, var(--mist-200) 0%, var(--mist-100) 60%, var(--mist-50) 100%)",
                  }}
                >
                  <div className="h-full overflow-hidden rounded-t-xl bg-white shadow-[inset_0_2px_8px_rgba(0,0,0,0.06)]">
                    <div className="space-y-3 p-5">
                      {[
                        {
                          caller: "Sarah Mitchell",
                          reason: "New inquiry",
                          outcome: "Booked Thu 2 pm",
                          duration: "2:34",
                          quote:
                            "I'd like to find out more about your services.",
                        },
                        {
                          caller: "Tom Pearce",
                          reason: "Appointment reschedule",
                          outcome: "Rescheduled to Fri 10 am",
                          duration: "1:21",
                          quote:
                            "Something came up — can we move it to later in the week?",
                        },
                      ].map(({ caller, reason, outcome, duration, quote }) => (
                        <div
                          key={caller}
                          className="rounded-lg border border-zinc-100 px-4 py-3.5"
                        >
                          <div className="mb-2.5 flex items-center justify-between">
                            <p className="text-[10px] font-semibold tracking-wider text-zinc-500 uppercase">
                              Call summary
                            </p>
                            <p className="text-[10px] text-zinc-400">
                              {duration}
                            </p>
                          </div>
                          <div className="mb-2.5 space-y-1.5">
                            {[
                              { label: "Caller", value: caller },
                              { label: "Reason", value: reason },
                              { label: "Outcome", value: outcome },
                            ].map(({ label, value }) => (
                              <div
                                key={label}
                                className="flex items-baseline gap-2"
                              >
                                <span className="w-14 shrink-0 text-[10px] text-zinc-400">
                                  {label}
                                </span>
                                <span className="text-xs font-medium text-zinc-900">
                                  {value}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="border-t border-zinc-100 pt-2.5">
                            <p className="text-[10px] leading-relaxed text-zinc-400 italic">
                              &ldquo;{quote}&rdquo;
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <h3 className="mb-2 text-xl font-semibold text-zinc-900">
                  Always in the loop
                </h3>
                <p className="text-sm leading-relaxed text-zinc-500">
                  A person at the front desk only passes along what they
                  remember. Your receptionist logs every call in full so nothing
                  gets lost in the cracks.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-6xl px-6">
            <div className="mb-16">
              <p className="text-mist-600 mb-3 text-sm font-medium">Features</p>
              <h2
                className="mb-2 text-3xl leading-snug font-medium tracking-tight text-zinc-900 lg:text-4xl"
                style={{ fontFamily: "'Hedvig Letters Serif', serif" }}
              >
                Everything a receptionist can do
              </h2>
              <p className="max-w-xl leading-relaxed text-zinc-500">
                The whole job, handled automatically.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3">
              {[
                {
                  icon: PhoneForwarded,
                  title: "Call transfers",
                  body: "Routes callers to the right person on your team, the moment they need one.",
                },
                {
                  icon: Voicemail,
                  title: "Message taking",
                  body: "Captures caller details and delivers them to you immediately via email or Slack.",
                },
                {
                  icon: CalendarDays,
                  title: "Appointment booking",
                  body: "Schedules directly into your calendar so no opportunity falls through the cracks.",
                },
                {
                  icon: Globe,
                  title: "Multi-lingual",
                  body: "Answers fluently in English, Spanish, and a range of other languages.",
                },
                {
                  icon: AlignLeft,
                  title: "Instant transcripts",
                  body: "Every call logged word for word. Searchable, on record, and always accessible.",
                },
                {
                  icon: ScrollText,
                  title: "Trained on your business",
                  body: "Knows your hours, services, and other details so callers get accurate answers on the spot.",
                },
              ].map(({ icon: Icon, title, body }, i) => (
                <div
                  key={title}
                  className={cn(
                    "flex flex-col items-center gap-5 px-8 py-12 text-center",
                    i % 3 !== 2 && "lg:border-r lg:border-zinc-100",
                    i < 5 && "border-b border-zinc-100",
                    i >= 3 && "lg:border-b-0",
                  )}
                >
                  <div className="bg-mist-50 border-mist-100 flex size-14 items-center justify-center rounded-2xl border">
                    <Icon className="text-mist-600 size-6" />
                  </div>
                  <div>
                    <h3 className="mb-2 font-semibold text-zinc-900">
                      {title}
                    </h3>
                    <p className="mx-auto max-w-[220px] text-sm leading-relaxed text-zinc-500">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-2xl px-6">
            <p className="text-mist-600 mb-3 text-center text-sm font-medium">
              FAQ
            </p>
            <h2
              className="mb-12 text-center text-3xl leading-snug font-medium tracking-tight text-zinc-900 lg:text-4xl"
              style={{ fontFamily: "'Hedvig Letters Serif', serif" }}
            >
              Common questions
            </h2>
            <Accordion
              type="single"
              collapsible
              className="divide-y divide-zinc-100"
            >
              {[
                {
                  q: "Will it sound like a robot?",
                  a: "No. Your receptionist handles natural back-and-forth, pauses, and interruptions the way a real conversation flows. Try the demo above and see for yourself.",
                },
                {
                  q: "What if a caller needs a real person?",
                  a: "It transfers them. You set the rules — which types of calls get routed and to whom — so urgent calls, pricing conversations, or anything that needs a human goes straight to the right person. Your receptionist handles the routing; your team takes it from there.",
                },
                {
                  q: "What if it's asked something it doesn't know?",
                  a: "It says so honestly, then offers to help another way — taking a message, booking a callback, or transferring to your team. It won't guess or make something up. If a topic keeps coming up, you can add it to its knowledge and it'll have the answer going forward.",
                },
                {
                  q: "Can I change what it says?",
                  a: "Yes. It's automatically trained on your business — your hours, services, and FAQs. If anything needs updating later, changes take effect immediately. It's your receptionist, trained your way.",
                },
                {
                  q: "Can I keep my existing phone number?",
                  a: "Yes. Forward your existing number to your receptionist and everything routes through without your callers noticing a change. Your number stays yours.",
                },
                {
                  q: "Is there a contract?",
                  a: "No. Cancel anytime, no questions asked.",
                },
              ].map(({ q, a }) => (
                <AccordionItem key={q} value={q} className="border-none py-1">
                  <AccordionTrigger className="py-4 text-left text-sm font-medium text-zinc-900 hover:no-underline">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 text-sm leading-relaxed text-zinc-500">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-6xl px-6">
            <div
              className="relative flex flex-col items-center gap-6 overflow-hidden rounded-2xl border border-black/5 px-12 py-16 text-center"
              style={{
                background:
                  "radial-gradient(ellipse at 30% 60%, var(--mist-200) 0%, var(--mist-100) 50%, var(--mist-50) 100%)",
              }}
            >
              <h2
                className="relative text-3xl font-medium text-zinc-900"
                style={{ fontFamily: "'Hedvig Letters Serif', serif" }}
              >
                Ready to put this to work for {businessName}?
              </h2>
              <p className="relative max-w-md leading-relaxed text-zinc-600">
                Takes minutes to set up and goes live immediately.
              </p>
              {account.cta_enabled && !partnerManaged ? (
                <div className="relative flex flex-col items-center gap-3">
                  <Button
                    size="lg"
                    className="bg-zinc-900 text-white shadow-sm hover:bg-zinc-700"
                    disabled={!canActivate}
                    onClick={openActivate}
                  >
                    {trial
                      ? "Start your free trial"
                      : "Activate your receptionist"}
                  </Button>
                  {trial && (
                    <p className="text-sm text-zinc-600">
                      {trial.days}-day free trial, then billed monthly. Cancel
                      anytime.
                    </p>
                  )}
                </div>
              ) : (
                <Button
                  size="lg"
                  className="relative bg-zinc-900 text-white shadow-sm hover:bg-zinc-700"
                  disabled={!partner?.support_email}
                  asChild={Boolean(partner?.support_email)}
                >
                  {partner?.support_email ? (
                    <a
                      href={`mailto:${partner.support_email}`}
                      onClick={() => trackCta("contact_us")}
                    >
                      Get in touch
                    </a>
                  ) : (
                    <span>Get in touch</span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-zinc-100 bg-white">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
            <Brand partner={partner} className="text-sm text-zinc-400" />
            <p className="text-xs text-zinc-300">
              &copy; {new Date().getFullYear()}
            </p>
          </div>
        </footer>
      </div>

      {!partnerManaged && canActivate && pricing && stripe && (
        <ActivateAgentModal
          open={activateOpen}
          onOpenChange={setActivateOpen}
          landingUid={uid}
          agentName={`${businessName} receptionist`}
          retailPriceCents={pricing.retail_price_cents}
          currency={pricing.currency}
          includedMinutes={pricing.included_minutes}
          overageRetailPerMinute={pricing.overage_retail_per_minute}
          trial={pricing.trial}
          publishableKey={stripe.publishable_key}
          connectedAccountId={stripe.connected_account_id}
        />
      )}
    </>
  )
}
